import { ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

type Props = {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** Footer slot — typically holds the action buttons. */
  footer?: ReactNode;
  /** Visual width preset. */
  size?: "sm" | "md" | "lg";
  /** When true, clicking the backdrop closes the modal. Default true. */
  closeOnBackdrop?: boolean;
  children: ReactNode;
};

/**
 * Generic modal — renders into document.body via a portal so it sits above
 * everything regardless of the page tree. Esc closes; the backdrop is
 * blurred to match the file-preview / login aesthetic.
 */
export function Modal({
  open, onClose, title, footer, size = "md", closeOnBackdrop = true, children,
}: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="modal-overlay"
      onClick={() => closeOnBackdrop && onClose()}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`modal-card modal-${size}`}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || true) && (
          <header className="modal-header">
            <h2 className="modal-title">{title}</h2>
            <button
              type="button"
              className="modal-close icon-btn"
              onClick={onClose}
              aria-label="Close"
            >
              <X className="icon-svg" />
            </button>
          </header>
        )}
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-footer">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
